import React from 'react';
import { Page, Text, View } from '@react-pdf/renderer';
import { formatCurrency, formatDecimal } from '../../../utils/format';
import { formatDate, styles } from './pdfShared';
import type { BusinessCustomerRevenueRow, BusinessPurchaseHistoryRow } from '../../../services/report.service';
import { ReportPdfLogo } from './ReportPdfLogo';
import type { TFn } from '../export/context';

interface Props {
  customerBreakdown: ReadonlyArray<BusinessCustomerRevenueRow>;
  history: ReadonlyArray<BusinessPurchaseHistoryRow>;
  t: TFn;
  currencySymbol: string;
}

// Customer report and sales report each render on their own dedicated page
// (the business overview page only carries the summary cards + revenue chart).
export const BusinessDetailPages: React.FC<Props> = ({ customerBreakdown, history, t, currencySymbol }) => (
  <>
    <Page size="A4" style={styles.page}>
      <ReportPdfLogo />
      <Text style={styles.title}>Customer Report</Text>
      <View style={styles.table}>
        <View style={[styles.row, styles.headerRow]}>
          <Text style={[styles.cell, styles.headText, { flexGrow: 50 }]}>Customer</Text>
          <Text style={[styles.cell, styles.headText, styles.right, { flexGrow: 22 }]}>Orders</Text>
          <Text style={[styles.cell, styles.headText, styles.right, { flexGrow: 28 }]}>Revenue</Text>
        </View>
        {customerBreakdown.map((row) => (
          <View key={row.customerId || row.customerName} style={styles.row}>
            <Text style={[styles.cell, { flexGrow: 50 }]}>{row.customerName}</Text>
            <Text style={[styles.cell, styles.right, { flexGrow: 22 }]}>{formatDecimal(row.orderCount, 0)}</Text>
            <Text style={[styles.cell, styles.right, { flexGrow: 28 }]}>{formatCurrency(row.totalRevenue, currencySymbol)}</Text>
          </View>
        ))}
      </View>
    </Page>
    <Page size="A4" style={styles.page}>
      <ReportPdfLogo />
      <Text style={styles.title}>Sales Report</Text>
      <View style={styles.table}>
        <View style={[styles.row, styles.headerRow]}>
          <Text style={[styles.cell, styles.headText, { flexGrow: 20 }]}>{t('reports.export.columns.orderNumber')}</Text>
          <Text style={[styles.cell, styles.headText, { flexGrow: 16 }]}>{t('reports.export.columns.orderDate')}</Text>
          <Text style={[styles.cell, styles.headText, { flexGrow: 30 }]}>{t('reports.export.columns.customerName')}</Text>
          <Text style={[styles.cell, styles.headText, styles.right, { flexGrow: 18 }]}>{t('reports.export.columns.totalAmount')}</Text>
          <Text style={[styles.cell, styles.headText, { flexGrow: 16 }]}>{t('reports.export.columns.status')}</Text>
        </View>
        {history.map((r) => (
          <View key={r.orderId} style={styles.row}>
            <Text style={[styles.cell, { flexGrow: 20 }]}>{r.orderNumber}</Text>
            <Text style={[styles.cell, { flexGrow: 16 }]}>{formatDate(r.orderDate)}</Text>
            <Text style={[styles.cell, { flexGrow: 30 }]}>{r.customerName}</Text>
            <Text style={[styles.cell, styles.right, { flexGrow: 18 }]}>{formatCurrency(r.totalAmount, currencySymbol)}</Text>
            <Text style={[styles.cell, { flexGrow: 16 }]}>{r.status}</Text>
          </View>
        ))}
      </View>
    </Page>
  </>
);
